// React hooks for the admin editorial pages. Same two-mode pattern as
// the agent side: when VITE_ARTICLES_API is set the hooks hit
// /admin/articles*, otherwise they return the in-memory ARTICLES
// fixture from admin-mock.ts.

import { useEffect, useMemo, useState } from "react";
import {
  adaptAdminArticle,
  addNote,
  articlesApiEnabled,
  approveArticle,
  claimArticle,
  createAdminArticle,
  getAdminArticle,
  getQueueStats,
  listAdminArticles,
  patchAdminArticle,
  rejectArticle,
  releaseArticle,
  requestChanges,
  submitAdminArticle,
  type AdminListFilter,
  type ApproveInput,
  type ArticleWire,
  type DraftingAdmin,
  type PatchInput,
  type QueueStats,
  type ReviewEventWire,
  type RevisionWire,
} from "./articlesApi";
import { ARTICLES, type Article } from "./admin-mock";
import { HOUSE_AUTHOR_ID } from "@/constants/articles";
import type { KindKey } from "@/components/admin-ui";

// ── list ─────────────────────────────────────────────────────────────────

export function useAdminArticles(filter: AdminListFilter = {}): {
  rows: Article[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [rows, setRows] = useState<Article[]>(() =>
    articlesApiEnabled() ? [] : applyMockFilter(ARTICLES, filter),
  );
  const [total, setTotal] = useState<number>(() =>
    articlesApiEnabled() ? 0 : applyMockFilter(ARTICLES, filter).length,
  );
  const [loading, setLoading] = useState(articlesApiEnabled());
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const key = `${filter.state ?? ""}|${filter.kind ?? ""}|${filter.q ?? ""}|${filter.reviewer ?? ""}|${filter.page ?? ""}|${filter.limit ?? ""}`;

  useEffect(() => {
    if (!articlesApiEnabled()) {
      const filtered = applyMockFilter(ARTICLES, filter);
      setRows(filtered);
      setTotal(filtered.length);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    listAdminArticles(filter)
      .then((resp) => {
        if (cancelled) return;
        setRows(resp.articles.map((w) => adaptAdminArticle(w)));
        setTotal(resp.total);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick]);

  return { rows, total, loading, error, refresh: () => setTick((t) => t + 1) };
}

function applyMockFilter(rows: Article[], f: AdminListFilter): Article[] {
  const q = (f.q || "").toLowerCase();
  return rows.filter((a) => {
    if (f.state && a.state !== f.state) return false;
    if (f.kind && a.kind !== f.kind) return false;
    if (q && !a.title.toLowerCase().includes(q)) return false;
    return true;
  });
}

// ── single article ──────────────────────────────────────────────────────

export interface AdminArticleDetail {
  row: Article;
  wire?: ArticleWire;
  revisions?: RevisionWire[];
  events?: ReviewEventWire[];
  // draftedBy — the admin who composed a house article (PROP-56);
  // null for agent-authored articles. Admin-only, never public.
  draftedBy?: DraftingAdmin | null;
}

export function useAdminArticle(id: string | undefined): {
  detail: AdminArticleDetail | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [detail, setDetail] = useState<AdminArticleDetail | null>(() =>
    id && !articlesApiEnabled() ? mockDetail(id) : null,
  );
  const [loading, setLoading] = useState(articlesApiEnabled() && !!id);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!id) {
      setDetail(null);
      setLoading(false);
      return;
    }
    if (!articlesApiEnabled()) {
      setDetail(mockDetail(id));
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAdminArticle(id)
      .then((resp) => {
        if (cancelled) return;
        setDetail({
          row: adaptAdminArticle(resp.article),
          wire: resp.article,
          revisions: resp.revisions,
          events: resp.events,
          draftedBy: resp.drafted_by ?? null,
        });
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, tick]);

  return { detail, loading, error, refresh: () => setTick((t) => t + 1) };
}

function mockDetail(id: string): AdminArticleDetail | null {
  const row = ARTICLES.find((a) => a.id === id);
  if (!row) return null;
  return { row };
}

// ── queue stats ──────────────────────────────────────────────────────────

export function useQueueStats(): {
  stats: QueueStats;
  loading: boolean;
  refresh: () => void;
} {
  const fallback: QueueStats = useMemo(() => ({
    awaiting_review: ARTICLES.filter((a) => a.state === "awaiting_review").length,
    in_review: ARTICLES.filter((a) => a.state === "in_review").length,
    changes_requested: ARTICLES.filter((a) => a.state === "changes_requested").length,
    published: ARTICLES.filter((a) => a.state === "published").length,
    scheduled: ARTICLES.filter((a) => a.state === "scheduled").length,
    rejected: ARTICLES.filter((a) => a.state === "rejected").length,
  }), []);
  const [stats, setStats] = useState<QueueStats>(fallback);
  const [loading, setLoading] = useState(articlesApiEnabled());
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!articlesApiEnabled()) {
      setStats(fallback);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getQueueStats()
      .then((s) => {
        if (cancelled) return;
        setStats(s);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setStats(fallback);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick, fallback]);

  return { stats, loading, refresh: () => setTick((t) => t + 1) };
}

// ── moderation actions ──────────────────────────────────────────────────

export function useAdminActions() {
  return useMemo(
    () => ({
      enabled: articlesApiEnabled(),
      async claim(id: string) {
        if (!articlesApiEnabled()) return;
        await claimArticle(id);
      },
      async release(id: string) {
        if (!articlesApiEnabled()) return;
        await releaseArticle(id);
      },
      async requestChanges(id: string, note: string) {
        if (!articlesApiEnabled()) return;
        await requestChanges(id, note);
      },
      async reject(id: string, note: string) {
        if (!articlesApiEnabled()) return;
        await rejectArticle(id, note);
      },
      async approve(id: string, input: ApproveInput) {
        if (!articlesApiEnabled()) return;
        await approveArticle(id, input);
      },
      async addNote(id: string, body: string, anchor = "") {
        if (!articlesApiEnabled()) return;
        await addNote(id, body, anchor);
      },
    }),
    [],
  );
}

// ── DataLab compose (PROP-52) ────────────────────────────────────────────

// useComposeActions wraps the house-authoring writes. Mirrors the
// no-op-when-disabled shape of useAdminActions so the compose page can
// be exercised in offline-mock dev without a backend.
export function useComposeActions() {
  return useMemo(
    () => ({
      enabled: articlesApiEnabled(),
      async create(kind: KindKey): Promise<ArticleWire | null> {
        if (!articlesApiEnabled()) return null;
        return createAdminArticle({ kind });
      },
      async save(id: string, patch: PatchInput): Promise<ArticleWire | null> {
        if (!articlesApiEnabled()) return null;
        return patchAdminArticle(id, patch);
      },
      async submit(id: string): Promise<ArticleWire | null> {
        if (!articlesApiEnabled()) return null;
        return submitAdminArticle(id);
      },
    }),
    [],
  );
}

// useHouseDrafts lists the resumable DataLab drafts — house-authored
// articles still in draft / changes_requested. The author filter is
// client-side: the admin list endpoint has no author param, and at
// editorial volumes one page of drafts is cheap to scan.
export function useHouseDrafts(): {
  drafts: ArticleWire[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [drafts, setDrafts] = useState<ArticleWire[]>([]);
  const [loading, setLoading] = useState(articlesApiEnabled());
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!articlesApiEnabled()) {
      setDrafts([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    listAdminArticles({ limit: 100 })
      .then((resp) => {
        if (cancelled) return;
        setDrafts(
          resp.articles.filter(
            (w) =>
              w.author_id === HOUSE_AUTHOR_ID &&
              (w.state === "draft" || w.state === "changes_requested"),
          ),
        );
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { drafts, loading, error, refresh: () => setTick((t) => t + 1) };
}

// Re-export so pages can call from one import.
export { adaptAdminArticle } from "./articlesApi";
